import { useMemo, useState } from 'react'
import { ArrowDownLeft, ArrowRight, ArrowUpRight, FileCheck2, Network, Search } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import FdeRecordStatus from '@/frontend/features/Fde/FdeRecordStatus'
import type { OntologyInspectorProps } from '@/frontend/features/Fde/OntologyGraphInspector'
import type { ModelElement, Relationship } from '@/frontend/features/Fde/types'

type Direction = 'all' | 'outgoing' | 'incoming'

export default function OntologyInspectorRelationships(props: OntologyInspectorProps) {
  const [query, setQuery] = useState('')
  const [direction, setDirection] = useState<Direction>('all')
  const [verb, setVerb] = useState('all')
  const nodeId = props.selectedNode ? String(props.selectedNode.id) : null
  const endpointIds = props.selectedRelationship
    ? [String(props.selectedRelationship.from.id), String(props.selectedRelationship.to.id)]
    : []
  const available = nodeId
    ? props.nodeRelationships
    : props.relationships.filter((item) => item.id !== props.selectedRelationship?.id
      && endpointIds.some((id) => id === String(item.from.id) || id === String(item.to.id)))
  const verbs = useMemo(
    () => [...new Set(available.map((item) => item.verb))].sort((left, right) => left.localeCompare(right, 'pt-BR')),
    [available],
  )
  const normalizedQuery = query.trim().toLocaleLowerCase('pt-BR')
  const filtered = available.filter((item) => {
    const itemDirection = String(item.from.id) === nodeId ? 'outgoing' : 'incoming'
    if (nodeId && direction !== 'all' && direction !== itemDirection) return false
    if (verb !== 'all' && item.verb !== verb) return false
    return !normalizedQuery || `${item.from.name} ${item.verb} ${item.to.name} ${item.description || ''}`
      .toLocaleLowerCase('pt-BR')
      .includes(normalizedQuery)
  })

  return (
    <div className="space-y-3">
      {props.selectedRelationship && <EndpointContext {...props} />}
      <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_9.75rem_10rem]">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={props.copy.workspace.inspector_search_connections} className="h-8 pl-8 text-xs" />
        </div>
        {nodeId && <Select value={direction} onValueChange={(value) => setDirection(value as Direction)}>
          <SelectTrigger className="h-8 w-full text-xs" aria-label={props.copy.workspace.inspector_filter_direction}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{props.copy.workspace.inspector_all_directions}</SelectItem>
            <SelectItem value="outgoing">{props.copy.workspace.inspector_outgoing}</SelectItem>
            <SelectItem value="incoming">{props.copy.workspace.inspector_incoming}</SelectItem>
          </SelectContent>
        </Select>}
        <Select value={verb} onValueChange={setVerb}>
          <SelectTrigger className="h-8 w-full text-xs" aria-label={props.copy.workspace.inspector_filter_verb}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{props.copy.workspace.inspector_all_verbs}</SelectItem>
            {verbs.map((item) => <SelectItem key={item} value={item}>{item}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center justify-between px-0.5 text-[10px] text-muted-foreground">
        <span className="tabular-nums">{filtered.length} {props.copy.workspace.inspector_of} {available.length} {props.copy.workspace.relationships.toLocaleLowerCase('pt-BR')}</span>
      </div>
      {filtered.length === 0
        ? <EmptyRelationships copy={props.copy} />
        : <div data-testid="ontology-relationship-list" className="w-full max-w-full divide-y divide-border overflow-hidden rounded-lg border border-border bg-background">
          {filtered.map((relationship) => (
            <ConnectionRow
              key={relationship.id}
              relationship={relationship}
              selectedNodeId={nodeId}
              elements={props.elements}
              copy={props.copy}
              onSelectRelationship={props.onSelectRelationship}
              onSelectNode={props.onSelectNode}
            />
          ))}
        </div>}
    </div>
  )
}

function EndpointContext(props: OntologyInspectorProps) {
  const relationship = props.selectedRelationship!
  return <section>
    <h3 className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">{props.copy.workspace.inspector_endpoints}</h3>
    <div className="grid grid-cols-2 gap-2">{[relationship.from, relationship.to].map((endpoint, index) => {
      const element = props.elements.find((item) => String(item.id) === String(endpoint.id))
      const count = props.relationships.filter((item) => [item.from.id, item.to.id].some((id) => String(id) === String(endpoint.id))).length
      return <button key={endpoint.id} type="button" onClick={() => props.onSelectNode(String(endpoint.id))} className="min-w-0 rounded-lg border border-border bg-muted/10 px-3 py-2 text-left hover:border-primary/40">
        <span className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">{index === 0 ? props.copy.workspace.inspector_from : props.copy.workspace.inspector_to}</span>
        <span className="mt-0.5 block truncate text-xs font-semibold text-foreground">{endpoint.name}</span>
        <span className="mt-0.5 block truncate text-[10px] text-muted-foreground">{element?.ontology_class?.name || element?.kind} · {count} {props.copy.workspace.relationships.toLocaleLowerCase('pt-BR')}</span>
      </button>
    })}</div>
  </section>
}

function ConnectionRow({ relationship, selectedNodeId, elements, copy, onSelectRelationship, onSelectNode }: {
  relationship: Relationship
  selectedNodeId: string | null
  elements: ModelElement[]
  copy: OntologyInspectorProps['copy']
  onSelectRelationship: (id: string) => void
  onSelectNode: (id: string) => void
}) {
  const outgoing = !selectedNodeId || String(relationship.from.id) === selectedNodeId
  const relatedId = selectedNodeId ? (outgoing ? relationship.to.id : relationship.from.id) : relationship.to.id
  const related = elements.find((item) => String(item.id) === String(relatedId))
  const relatedName = related?.name || (outgoing ? relationship.to.name : relationship.from.name)
  const relatedType = related?.ontology_class?.name || related?.kind || relationship.cardinality_label
  const hasContext = Boolean(relationship.description) || relationship.evidences.length > 0

  return <article data-testid="ontology-relationship-row" className="group flex w-full max-w-full min-w-0 items-stretch overflow-hidden transition-colors hover:bg-muted/20">
    <button type="button" onClick={() => onSelectRelationship(String(relationship.id))} className="min-w-0 flex-1 overflow-hidden px-3 py-2.5 text-left outline-none focus-visible:bg-primary/5">
      <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1 sm:grid-cols-[auto_minmax(0,1fr)_auto]">
        <div className="col-span-2 flex min-w-0 items-center gap-2 sm:col-span-1">
          <DirectionLabel outgoing={outgoing} copy={copy} />
          <span className="h-3 w-px shrink-0 bg-border" />
          <span className="max-w-28 truncate text-[10px] font-semibold uppercase tracking-wide text-primary" title={relationship.verb}>{relationship.verb}</span>
        </div>
        <span className="min-w-0 truncate text-sm font-semibold text-foreground" title={relatedName}>{relatedName}</span>
        <div className="flex min-w-0 shrink-0 items-center justify-end gap-2">
          <span className="hidden max-w-24 truncate text-[10px] text-muted-foreground md:block" title={relatedType}>{relatedType}</span>
          <FdeRecordStatus status={relationship.status} proposedLabel={copy.workspace.proposed} confirmedLabel={copy.workspace.confirmed_status} />
        </div>
      </div>
      {hasContext && <div className="mt-1.5 flex min-w-0 items-center gap-2 pl-0.5">
        <p className="min-w-0 flex-1 truncate text-[11px] leading-4 text-muted-foreground" title={relationship.description || undefined}>{relationship.description}</p>
        <span className="flex shrink-0 items-center gap-1 text-[10px] tabular-nums text-muted-foreground" title={copy.workspace.inspector_evidences}>
          <FileCheck2 className="size-3" />{relationship.evidences.length}
        </span>
      </div>}
    </button>
    {related && <button type="button" onClick={() => onSelectNode(String(related.id))} aria-label={copy.workspace.inspector_open_node} title={copy.workspace.inspector_open_node} className="flex w-10 shrink-0 items-center justify-center border-l border-border/70 text-muted-foreground outline-none transition-colors hover:bg-primary/5 hover:text-primary focus-visible:bg-primary/5 focus-visible:text-primary">
      <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-0.5" />
    </button>}
  </article>
}

function DirectionLabel({ outgoing, copy }: { outgoing: boolean; copy: OntologyInspectorProps['copy'] }) {
  return <span className="flex shrink-0 items-center gap-1 text-[9px] font-medium text-muted-foreground">
    {outgoing ? <ArrowUpRight className="size-3" /> : <ArrowDownLeft className="size-3" />}
    <span className="truncate">{outgoing ? copy.workspace.inspector_outgoing : copy.workspace.inspector_incoming}</span>
  </span>
}

function EmptyRelationships({ copy }: { copy: OntologyInspectorProps['copy'] }) {
  return <div className="rounded-lg border border-dashed border-border px-5 py-8 text-center">
    <Network className="mx-auto size-5 text-muted-foreground" />
    <p className="mt-2 text-sm text-muted-foreground">{copy.workspace.inspector_no_connections}</p>
  </div>
}
